"use client";

import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import {
  Upload,
  Check,
  Loader2,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { reportClientError } from "@/lib/observability/report-client-error";
import type { TruncatedPlatformResult } from "@/lib/demo/truncate";
import type { PlatformId } from "@/types/platforms";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB
const MAX_DURATION = 30; // seconds
const ACCEPTED_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

type DemoState =
  | "idle"
  | "probing"
  | "extracting"
  | "analyzing"
  | "results"
  | "error"
  | "rate-limited";

type ErrorKind = "tooLarge" | "tooLong" | "unsupported" | "network" | "failed";

/** Icon color per platform. */
const PLATFORM_ICON_COLOR: Record<PlatformId, string> = {
  youtube: "#FF0000",
  tiktok: "#FFFFFF",
  instagram: "#ff7a18",
  facebook: "#10b981",
  linkedin: "#059669",
  x: "#FFFFFF",
};

const PLATFORM_LABELS: Record<PlatformId, string> = {
  youtube: "YOUTUBE",
  tiktok: "TIKTOK",
  instagram: "INSTAGRAM",
  facebook: "FACEBOOK",
  linkedin: "LINKEDIN",
  x: "X",
};

const ALL_PLATFORMS: PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "linkedin",
  "x",
];

// ---------------------------------------------------------------------------
// Platform icons (inline SVGs for zero-dependency rendering)
// ---------------------------------------------------------------------------

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const PLATFORM_ICON_COMPONENTS: Record<PlatformId, React.ComponentType> = {
  youtube: YouTubeIcon,
  tiktok: TikTokIcon,
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    () => false
  );
}

// ---------------------------------------------------------------------------
// Sub-component: DropZone
// ---------------------------------------------------------------------------

function DropZone({
  onFile,
  error,
  t,
}: {
  onFile: (file: File) => void;
  error: ErrorKind | null;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
    [onFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onFile(file);
      // Reset so same file can be re-selected
      e.target.value = "";
    },
    [onFile]
  );

  const errorKey = error ? `error.${error}` : null;

  return (
    <div className="mx-auto max-w-md">
      <div
        role="button"
        tabIndex={0}
        aria-label={t("dropzone.button")}
        className={`focus-visible:ring-ring relative flex min-h-48 cursor-pointer touch-manipulation flex-col items-center justify-center rounded-[28px] border-2 border-dashed p-8 transition-colors focus-visible:ring-2 focus-visible:outline-none ${
          dragging
            ? "border-primary/60 bg-surface-success/75"
            : "border-border bg-card/65 hover:border-primary/35 hover:bg-card/80"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          name="homepage-demo-video"
          aria-label={t("dropzone.button")}
          onChange={handleChange}
        />
        <Upload
          className="text-muted-foreground mb-4 h-10 w-10"
          aria-hidden="true"
        />
        {/* Desktop title */}
        <p className="text-foreground hidden text-lg font-semibold md:block">
          {t("dropzone.title")}
        </p>
        {/* Mobile title */}
        <p className="text-foreground text-lg font-semibold md:hidden">
          {t("dropzone.titleMobile")}
        </p>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("dropzone.subtitle")}
        </p>
        <button
          type="button"
          className="border-border bg-accent text-accent-foreground focus-visible:ring-ring hover:bg-secondary mt-4 min-h-12 touch-manipulation rounded-xl border px-6 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          {t("dropzone.button")}
        </button>
        <div className="text-muted-foreground/80 mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
          <span>{t("dropzone.formats")}</span>
          <span className="hidden sm:inline">&middot;</span>
          <span>{t("dropzone.size")}</span>
          <span className="hidden sm:inline">&middot;</span>
          <span>{t("dropzone.noAccount")}</span>
        </div>
      </div>

      {/* Error message */}
      {errorKey && (
        <div
          role="alert"
          aria-live="polite"
          className="border-surface-danger-border bg-surface-danger text-destructive mt-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t(errorKey)}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ProcessingView
// ---------------------------------------------------------------------------

interface ProcessingStage {
  key: string;
  done: boolean;
}

function ProcessingView({
  stages,
  reducedMotion,
  t,
}: {
  stages: ProcessingStage[];
  reducedMotion: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  return (
    <div className="mx-auto max-w-md text-center">
      <Loader2
        className="text-primary mx-auto mb-6 h-10 w-10 animate-spin"
        aria-hidden="true"
      />
      <h3
        className="text-foreground text-xl font-semibold"
        style={{ textWrap: "balance" }}
      >
        {t("processing.title")}
      </h3>
      <p className="text-muted-foreground mt-2 text-sm">
        {t("processing.subtitle")}
      </p>

      <div className="mt-8 space-y-3 text-left" aria-live="polite">
        {stages.map((stage, i) => (
          <div
            key={stage.key}
            className="flex items-center gap-3 text-sm"
            style={
              reducedMotion
                ? undefined
                : {
                    opacity: stage.done ? 1 : 0.4,
                    transform: stage.done ? "translateY(0)" : "translateY(4px)",
                    transition: `opacity 300ms ${i * 100}ms, transform 300ms ${i * 100}ms`,
                  }
            }
          >
            {stage.done ? (
              <Check
                className="text-primary h-4 w-4 shrink-0"
                aria-hidden="true"
              />
            ) : (
              <Loader2
                className="text-muted-foreground h-4 w-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            )}
            <span
              className={
                stage.done ? "text-foreground" : "text-muted-foreground"
              }
            >
              {t(`processing.stage.${stage.key}`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ResultsView
// ---------------------------------------------------------------------------

function ResultsView({
  platforms,
  reducedMotion,
  onReset,
  t,
}: {
  platforms: TruncatedPlatformResult[];
  reducedMotion: boolean;
  onReset: () => void;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  // Sort platforms in canonical order
  const sorted = ALL_PLATFORMS.map((id) =>
    platforms.find((p) => p.platform === id)
  ).filter(Boolean) as TruncatedPlatformResult[];

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-8 text-center">
        <h3
          className="text-foreground text-xl font-semibold sm:text-2xl"
          style={{ textWrap: "balance" }}
        >
          {t("results.title")}
        </h3>
        <p className="text-muted-foreground mt-2 text-sm">
          {t("results.subtitle")}
        </p>
      </div>

      {/* All platform cards */}
      <div className="space-y-4">
        {sorted.map((p, i) => (
          <PlatformCard
            key={p.platform}
            result={p}
            index={i}
            reducedMotion={reducedMotion}
            t={t}
          />
        ))}
      </div>

      {/* CTA */}
      <div className="mt-10 text-center">
        <Button asChild size="lg" className="w-full md:w-auto">
          <Link href="/signup">
            {t("results.cta")}
            <ChevronRight
              className="ml-1 inline-block h-4 w-4"
              aria-hidden="true"
            />
          </Link>
        </Button>
        <p className="text-muted-foreground/80 mt-2 text-xs">
          {t("results.ctaSubtitle")}
        </p>
      </div>

      {/* Try another */}
      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onReset}
          className="text-muted-foreground focus-visible:ring-ring hover:text-foreground min-h-12 touch-manipulation text-sm underline underline-offset-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          {t("results.tryAnother")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-sub-component: PlatformCard (all platforms)
// ---------------------------------------------------------------------------

function PlatformCard({
  result,
  index,
  reducedMotion,
  t,
}: {
  result: TruncatedPlatformResult;
  index: number;
  reducedMotion: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
}) {
  const iconColor = PLATFORM_ICON_COLOR[result.platform];
  const IconComponent = PLATFORM_ICON_COMPONENTS[result.platform];

  return (
    <div
      className="border-border bg-surface-strong/90 overflow-hidden rounded-2xl border shadow-[var(--glass-shadow)] select-none"
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        ...(reducedMotion
          ? {}
          : {
              opacity: 0,
              transform: "translateY(12px)",
              animation: `demoCardIn 400ms ${index * 100}ms forwards`,
            }),
      }}
      onCopy={(e) => e.preventDefault()}
    >
      <div className="p-4 sm:p-5">
        {/* Platform header: icon + name + option count badge */}
        <div className="mb-3 flex items-center gap-2">
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center"
            style={{ color: iconColor }}
            aria-hidden="true"
          >
            <IconComponent />
          </span>
          <span className="text-foreground/90 text-xs font-bold tracking-wider">
            {PLATFORM_LABELS[result.platform]}
          </span>
          {result.optionCount > 1 && (
            <span className="border-surface-success-border bg-surface-success text-primary rounded-full border px-2.5 py-0.5 text-[11px] font-medium">
              {t("results.titleOptions", { count: result.optionCount })}
            </span>
          )}
        </div>

        {/* Title — right 1/3 blurred */}
        {result.title && (
          <h4
            className="text-foreground mb-1.5 text-[15px] leading-snug font-semibold"
            style={{
              maskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
            }}
          >
            {result.title}
          </h4>
        )}

        {/* Description — right 1/3 faded out */}
        {result.description && (
          <p
            className="text-muted-foreground text-sm leading-relaxed"
            style={{
              maskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
            }}
          >
            {result.description}
          </p>
        )}

        {/* Hashtags — right 1/3 faded out */}
        {result.hashtags.length > 0 && (
          <p
            className="text-primary mt-3 text-sm"
            style={{
              maskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 50%, transparent 85%)",
            }}
          >
            {result.hashtags.join(" ")}{" "}
            {result.hashtagCount > result.hashtags.length && (
              <span className="text-primary/55">
                {t("results.moreTags", {
                  count: result.hashtagCount - result.hashtags.length,
                })}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HomepageDemo() {
  const { t } = useJsonTranslation("HomepageDemo");
  const reducedMotion = useReducedMotion();

  const [state, setState] = useState<DemoState>("idle");
  const [error, setError] = useState<ErrorKind | null>(null);
  const [platforms, setPlatforms] = useState<TruncatedPlatformResult[]>([]);
  const [visualOnly, setVisualOnly] = useState(false);
  const [stages, setStages] = useState<ProcessingStage[]>([
    { key: "recognized", done: false },
    { key: "extracted", done: false },
    { key: "analyzing", done: false },
    { key: "generating", done: false },
  ]);

  // Track rate-limited state across resets
  const wasRateLimited = useRef(false);

  const markStage = useCallback((key: string) => {
    setStages((prev) =>
      prev.map((s) => (s.key === key ? { ...s, done: true } : s))
    );
  }, []);

  const resetStages = useCallback(() => {
    setStages([
      { key: "recognized", done: false },
      { key: "extracted", done: false },
      { key: "analyzing", done: false },
      { key: "generating", done: false },
    ]);
  }, []);

  const handleReset = useCallback(() => {
    if (wasRateLimited.current) {
      setState("rate-limited");
    } else {
      setState("idle");
      setError(null);
      setPlatforms([]);
      setVisualOnly(false);
      resetStages();
    }
  }, [resetStages]);

  const handleFile = useCallback(
    async (file: File) => {
      // --- Client-side validations ---
      // On some mobile browsers file.type can be empty; allow if the file
      // was selected through the video/* input (trust the OS picker).
      if (
        file.type &&
        !file.type.startsWith("video/") &&
        !ACCEPTED_TYPES.includes(file.type)
      ) {
        setError("unsupported");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError("tooLarge");
        return;
      }

      setError(null);
      setState("probing");
      resetStages();

      try {
        // --- 1. Probe ---
        const { probeClientVideo } =
          await import("@/lib/ai/client-analysis-pipeline");
        const metadata = await probeClientVideo(file);
        markStage("recognized");

        if (metadata.duration > MAX_DURATION) {
          setState("idle");
          setError("tooLong");
          return;
        }

        // --- 2. Extract audio (non-fatal on mobile) ---
        // If client-side extraction fails (common on mobile due to WASM/WebCodecs
        // limitations), proceed without audio — the server analyzes from metadata.
        setState("extracting");
        let audioBlob: Blob | null = null;
        try {
          const { extractClientAudio } =
            await import("@/lib/ai/client-analysis-pipeline");
          audioBlob = await extractClientAudio(file, metadata);
        } catch (extractErr) {
          // SENTRY FIX: VIDSEEDS-2RF — NoAudioTrackError: Video has no audio track
          // Date: 2026-04-23 | Agent: Claude Code
          // Root cause: NoAudioTrackError is thrown intentionally when the
          // uploaded video has no audio track (silent video). The homepage-demo
          // flow already continues gracefully with visual-only analysis, but
          // reported the graceful signal as an error-level Sentry event — this
          // is exactly the "fallback fired" pattern that should be a warning.
          // Fix: classify NoAudioTrackError as warning; keep observability
          // without page-level noise. Other extraction errors still error.
          const errName =
            extractErr instanceof Error ? extractErr.name : undefined;
          const isNoAudioTrack = errName === "NoAudioTrackError";
          reportClientError(extractErr, {
            source: "components.home.homepage-demo",
            feature: "demo",
            action: "extractAudio",
            ...(isNoAudioTrack && { severity: "warning" as const }),
            extra: {
              fileType: file.type,
              fileSizeMB: (file.size / (1024 * 1024)).toFixed(0),
              codec: metadata.codec,
              noAudioTrack: isNoAudioTrack,
            },
          });
          // Continue without audio — server handles no-audio gracefully
        }
        markStage("extracted");

        // --- 3. Send to server ---
        setState("analyzing");
        markStage("analyzing");

        const { generateFingerprint } = await import("@/lib/demo/fingerprint");
        const fingerprint = await generateFingerprint();

        const formData = new FormData();
        if (audioBlob && audioBlob.size > 0) {
          formData.append("audio", audioBlob, "audio.mp3");
        }
        formData.append(
          "videoMetadata",
          JSON.stringify({
            duration: metadata.duration,
            width: metadata.width,
            height: metadata.height,
            fps: metadata.fps,
            codec: metadata.codec,
          })
        );

        const response = await fetch("/api/demo/analyze/", {
          method: "POST",
          body: formData,
          headers: {
            "x-demo-fingerprint": fingerprint,
          },
        });

        if (response.status === 429) {
          wasRateLimited.current = true;
          setState("rate-limited");
          return;
        }

        if (!response.ok) {
          throw new Error(`Server error (${response.status})`);
        }

        const data = (await response.json()) as {
          platforms: TruncatedPlatformResult[];
          visualOnly?: boolean;
        };

        markStage("generating");
        setPlatforms(data.platforms);
        setVisualOnly(!!data.visualOnly);
        setState("results");
      } catch (err) {
        reportClientError(err, {
          source: "components.home.homepage-demo",
          feature: "demo",
          action: "processVideo",
        });

        // Distinguish network errors
        if (
          err instanceof TypeError &&
          (err.message.includes("fetch") || err.message.includes("network"))
        ) {
          setError("network");
        } else {
          setError("failed");
        }
        setState("error");
      }
    },
    [markStage, resetStages]
  );

  return (
    <div
      id="live-demo"
      data-home-block="live-demo"
      className="relative scroll-mt-24 overflow-hidden px-5 py-6 sm:px-8 sm:py-8"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(52,211,153,0.14),transparent_55%),radial-gradient(circle_at_bottom,rgba(255,122,24,0.12),transparent_45%)]" />

      {/* Keyframe animation — injected once */}
      <style>{`
        @keyframes demoCardIn {
          to { opacity: 1; transform: translateY(0); }
          }
      `}</style>

      <div className="mx-auto max-w-6xl">
        {/* Section heading (idle + dropzone states) */}
        {(state === "idle" || state === "error") && (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)] lg:items-center lg:gap-10">
            <div className="max-w-xl text-center lg:text-left">
              <h2
                className="text-foreground text-3xl font-bold text-balance sm:text-4xl"
                style={{ textWrap: "balance" }}
              >
                {t("title")}
              </h2>
              <p className="text-muted-foreground mt-4 text-sm sm:text-base">
                {t("subtitle")}
              </p>
              <div className="text-muted-foreground/80 mt-6 flex flex-wrap items-center justify-center gap-2.5 text-sm lg:justify-start">
                <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  {t("dropzone.formats")}
                </span>
                <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  {t("dropzone.size")}
                </span>
                <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  {t("dropzone.noAccount")}
                </span>
              </div>
            </div>
            <DropZone onFile={handleFile} error={error} t={t} />
          </div>
        )}

        {/* Processing view */}
        {(state === "probing" ||
          state === "extracting" ||
          state === "analyzing") && (
          <ProcessingView stages={stages} reducedMotion={reducedMotion} t={t} />
        )}

        {/* Results view */}
        {state === "results" && (
          <>
            {visualOnly && (
              <div className="mx-auto mb-4 max-w-2xl rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-2.5 text-center text-sm text-amber-400/90">
                {t("visualOnlyNote")}
              </div>
            )}
            <ResultsView
              platforms={platforms}
              reducedMotion={reducedMotion}
              onReset={handleReset}
              t={t}
            />
          </>
        )}

        {/* Rate limit view */}
        {state === "rate-limited" && (
          <div className="mx-auto max-w-md text-center">
            <h3
              className="text-foreground text-xl font-semibold"
              style={{ textWrap: "balance" }}
              aria-live="polite"
            >
              {t("rateLimit.title")}
            </h3>
            <div className="mt-6">
              <Button asChild size="lg" className="w-full md:w-auto">
                <Link href="/signup">
                  {t("rateLimit.cta")}
                  <ChevronRight
                    className="ml-1 inline-block h-4 w-4"
                    aria-hidden="true"
                  />
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Error state with retry / network */}
        {state === "error" && error === "network" && (
          <div className="mt-4 text-center" aria-live="polite">
            <Button type="button" onClick={handleReset} variant="secondary">
              {t("error.retry")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
